import React, { useEffect, useState } from 'react';
import useScrollIntoView from './useScrollIntoView';

const DEFAULT_STEPS = [
  'Uploading your image...',
  'Sending it to the AI model...',
  'The model is composing your image...',
  'Finishing up...'
];

/**
 * Mounted only while a request is in flight. It owns its own timer, so
 * mounting it starts the elapsed counter and unmounting it stops it.
 */
const GenerationProgress = ({
  title = 'Generating your image',
  steps = DEFAULT_STEPS,
  estimateSeconds = 20,
  onCancel
}) => {
  const [elapsed, setElapsed] = useState(0);
  const containerRef = useScrollIntoView(true);

  useEffect(() => {
    const startedAt = Date.now();
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const stepList = steps.length ? steps : DEFAULT_STEPS;
  const secondsPerStep = Math.max(4, Math.round(estimateSeconds / stepList.length));
  const stepIndex = Math.min(stepList.length - 1, Math.floor(elapsed / secondsPerStep));
  // Creeps towards 95% over the expected duration - it never claims to be done.
  const percent = Math.min(95, Math.round((elapsed / Math.max(1, estimateSeconds)) * 90) + 5);
  const takingLong = elapsed > estimateSeconds * 1.75;

  return (
    <div className="generation-progress" ref={containerRef} role="status" aria-live="polite">
      <div className="generation-progress-header">
        <span className="spinner generation-progress-spinner" />
        <h3>{title}</h3>
      </div>

      <div className="generation-progress-bar">
        <div className="generation-progress-fill" style={{ width: `${percent}%` }} />
      </div>

      <p className="generation-progress-step">{stepList[stepIndex]}</p>
      <p className="generation-progress-meta">
        {elapsed}s elapsed
        {takingLong
          ? ' - this one is taking longer than usual, it is still running.'
          : ` - these usually take about ${estimateSeconds} seconds.`}
      </p>
      <p className="generation-progress-hint">Please keep this tab open. You are only charged once the image is ready.</p>

      <div className="generation-skeleton" aria-hidden="true" />

      {onCancel && (
        <button type="button" className="btn btn-secondary generation-progress-cancel" onClick={onCancel}>
          Cancel
        </button>
      )}
    </div>
  );
};

export default GenerationProgress;
